import 'sass/blocks/_header.scss';
// @ vendors
import React, { Fragment, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import Storage from 'utilities/storage';
// @ components
import ActionLink from 'components/actionLink';
import { LoginModal } from 'components/commons/loginForm/LoginModal';
import ConditionalToggling from 'utilities/toggling/ConditionalToggling/ConditionalToggling';
// @ utilities
import classNames from 'classnames';
import { isMobile } from 'utilities/browser';
import { formatText, FormattedText } from 'core/i18n';
import { checkUserInvestmentTypeAndReturnActiveOne } from 'utilities/userProfile';
import { buildPartnerPrivateUrl, buildLoginURLByEnv } from 'utilities/buildURLs';
// @ actions
import modalCancelActions from 'actions/modalCancelActions';
import headerActions from 'actions/headerActions';
// @ constants
import { PUBLICA_URL, API_APPLICATION, LOGIN_DESKTOP, LOGIN_MOBILE } from 'constants/index';
import { CREDIT_CARD } from 'constants/userProfile';
import { ACTIVATE_NEW_LOGIN, DISPLAY_LOGIN_LINK } from 'constants/featureFlagging';
import { recordEventCustomOnly } from 'utilities/uTagging';

// @ HOCS
import withFeatureToggling from 'hocs/withFeatureToggling/withFeatureToggling';
import { useEventListener } from '../../hooks/useEventListener';
import { Logo } from 'components/header/Logo/Logo';

const Header = ({
    bigHeader,
    headerActions,
    modalCancelActions,
    userProfile,
    partner,
    hiddenContinueProcess,
    flags,
    header,
    immSuitabilityTest,
    showContinueProcess,
    sticky,
}) => {
    const [scrolled, setScrolled] = useState(false);
    const [mobile, setMobile] = useState(false);
    const [showLoginForm, setShowLoginForm] = useState(false);

    const handleScroll = () => {
        const top = document?.documentElement?.scrollTop || document.body.scrollTop;
        setScrolled(top > 50);
    };

    const handleResize = () => {
        const width = window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth;
        setMobile(width < 991);
    };

    useEventListener('resize', !bigHeader ? handleResize : () => {});
    useEventListener('scroll', !bigHeader ? handleScroll : () => {});

    useEffect(() => {
        headerActions.getLogo();
        handleResize();
    }, []);

    const handleModalCancel = () => {
        modalCancelActions.showModalCancel();
    };

    const leaveTagEvent = () => {
        const categoriaEvento = userProfile.isAres ? 'interaccion en datos personales' : '/onboarding/abre-tu-cuenta/';
        recordEventCustomOnly({
            accionEvento: 'area de clientes',
            categoriaEvento,
        });
    };

    const handleClickShowLogin = () => {
        leaveTagEvent();
        setShowLoginForm(prevState => !prevState);
    };

    const handleClickRedirectLogin = () => {
        leaveTagEvent();
        const mobileLoginToggleString = isMobile ? LOGIN_MOBILE : LOGIN_DESKTOP;
        location.href = `${PUBLICA_URL}${mobileLoginToggleString}`;
    };

    const handleRedirectLoginSuccess = (response, defaultLoginSuccess) => {
        if (response.application === API_APPLICATION) {
            location.href = buildPartnerPrivateUrl(partner.get('id'));

            return;
        }

        defaultLoginSuccess();
    };

    const handleClickNewLogin = () => {
        leaveTagEvent();
        const loginURL = buildLoginURLByEnv();
        const privatePartnerURL = buildPartnerPrivateUrl(partner.get('id'));
        const loginURLWithRedirectionToProduct = `${loginURL}?destination=${privatePartnerURL}`;
        location.href = loginURLWithRedirectionToProduct;
    };

    const renderContinueProcess = userInvestmentType => {
        const tcDisplayLoginLink = flags.get(DISPLAY_LOGIN_LINK);
        const isNewLoginActivated = flags.get(ACTIVATE_NEW_LOGIN);

        if (hiddenContinueProcess) return null;
        if (!bigHeader) {
            return (
                <div className="header-process">
                    <hr />
                    <ConditionalToggling condition={!userInvestmentType[CREDIT_CARD]}>
                        <p className="header-process-text">
                            <FormattedText value="header-continueProcess" />
                            <span className="header-process-text-link" onClick={handleClickRedirectLogin}>
                                {formatText('header-link')}
                            </span>
                        </p>
                    </ConditionalToggling>
                    <ConditionalToggling condition={userInvestmentType[CREDIT_CARD] && tcDisplayLoginLink}>
                        <p className="header-process-text">
                            <FormattedText value="header-continueProcess" />
                            <span
                                className="header-process-text-link"
                                onClick={isNewLoginActivated ? handleClickNewLogin : handleClickShowLogin}
                            >
                                {formatText('header-link')}
                            </span>
                        </p>
                    </ConditionalToggling>
                </div>
            );
        }

        return null;
    };

    const getContinueProcess = () => {
        const userInvestmentType = checkUserInvestmentTypeAndReturnActiveOne(userProfile.investmentType);

        return showContinueProcess ? renderContinueProcess(userInvestmentType) : null;
    };

    const renderLogout = () => {
        const shouldRenderLogout =
            Storage.cookies.get('accessToken') && (!userProfile.testStarted || immSuitabilityTest.get('testCompleted'));

        if (!shouldRenderLogout) {
            return null;
        }

        return (
            <ActionLink onClick={handleModalCancel}>
                <span className="icons icons--icon-cancel-header" />
            </ActionLink>
        );
    };

    const isSmallHeaderStyle = isMobile() && header.get('isSmallHeaderStyle');
    
    const getHeaderClassName = () => {
        return classNames('header', {
            'header--collapsed': !isSmallHeaderStyle && scrolled,
            'header--small': isSmallHeaderStyle,
            hidden: mobile && header.get('headerHidden'),
            'header--no-content': true,
            'header--big-content': bigHeader,
            'header--sticky': sticky,
        });
    };

    const getHeaderImage = () => {
        const headerClassName = getHeaderClassName();
        const logo = header?.getIn(['logo', 'logo']);
        const icon = header?.getIn(['logo', 'icon']);

        if(isSmallHeaderStyle) return logo;

        return headerClassName === 'header header--collapsed header--no-content' ||
            headerClassName === 'header header--collapsed'
            ? icon
            : logo;
    };

    return (
        <Fragment>
            <header className={getHeaderClassName()} id="stepsHeader">
                <div className="header-actions">
                    <Logo href={PUBLICA_URL} imageUrl={getHeaderImage()} />
                    <div className="header-actions-cancel">{renderLogout()}</div>
                </div>
                {getContinueProcess()}
            </header>
            <LoginModal
                show={showLoginForm}
                onClose={handleClickShowLogin}
                onLoginSuccess={handleRedirectLoginSuccess}
            />
        </Fragment>
    );
};
Header.defaultProps = {
    bigHeader: false,
    showContinueProcess: true,
};

module.exports.constructor = Header;
module.exports.Header = connect(
    state => ({
        userProfile: state.userProfile,
        header: state.header,
        partner: state.products.get('partner'),
        immSuitabilityTest: state.raSuitabilityTest,
    }),
    dispatch => ({
        modalCancelActions: bindActionCreators(modalCancelActions, dispatch),
        headerActions: bindActionCreators(headerActions, dispatch),
    })
)(withFeatureToggling(Header));
